import { useEffect, useState } from 'react';
import { uid } from './model';

// master data: staff + devices (stored on the device), presets per sector
const KEY = 'giss_crew';
export const DEVICE_PRESETS = {
  detect_water: ['Korrelator', 'Bodenmikrofon', 'Geräuschlogger', 'Drucklogger', 'Ultraschall-Durchflussmesser (Clamp-on)', 'Prüfgas-Detektor H₂/N₂', 'Leitungsortungsgerät', 'Hydranten-Standrohr mit Zähler', 'Manometer / Druckschreiber', 'Inspektionskamera'],
  detect_gas: ['Gasspürgerät (FID/HLD)', 'Gaskonzentrations-Messgerät', 'Sondierstab / Bohrstock', 'Leitungsortungsgerät', 'KKS-Messgerät', 'Prüfgas-Detektor'],
  detect_cable: ['Kabelfehlerortung (TDR/Reflektometer)', 'Stoßspannungsgenerator', 'Mantelfehlerortung', 'Kabelsuch- / Leitungsortungsgerät', 'Isolationsmessgerät', 'Bodenmikrofon (Kabel)'],
  detect_sewer: ['Kanal-TV-Kamera (Fahrwagen)', 'Schiebekamera', 'Kanalspiegel', 'Nebelgerät', 'Dichtheitsprüfgerät', 'Gaswarngerät'],
  detect_inhouse: ['Wärmebildkamera', 'Feuchtemessgerät', 'Tracergas-Detektor (H₂/N₂)', 'Elektroakustisches Ortungsgerät', 'Druckprüfpumpe', 'Endoskop'],
  detect_heat: ['Leckwarn-Messgerät / Impulsreflektometer', 'Wärmebildkamera / Drohne', 'Bodenmikrofon', 'Tracergas-Detektor (H₂/N₂)', 'Leitungsortungsgerät', 'Druckprüfgerät'],
  detect_roof: ['Impulsstrom-Messgerät', 'Hochspannungs-Prüfgerät', 'Rauchgasgenerator', 'Tracergas-Detektor (H₂/N₂)', 'Wärmebildkamera / Drohne', 'Feuchtemessgerät'],
  detect_locate: ['Leitungsortungsgerät (Empfänger)', 'Sender / Generator', 'Sonde', 'Bodenradar (GPR)', 'GNSS/RTK-Rover', 'Markierspray'],
  pipe: ['Leitungsortungsgerät', 'GNSS/RTK-Rover', 'Tachymeter', 'Rohrlaser', 'Kanal-TV-Kamera', 'Druckprüfgerät', 'Nivelliergerät', 'Bagger', 'Rüttelplatte'],
  garden: ['Traktor', 'Pflanzenschutzspritze', 'Düngerstreuer', 'Freischneider', 'Motorsäge', 'Rasenmäher', 'Minibagger', 'GNSS/RTK-Rover', 'Bodenfeuchte-Messgerät'],
  building: ['Laser-Entfernungsmesser', 'Tachymeter', 'Feuchtemessgerät', 'Wärmebildkamera', 'Drohne'],
  survey: ['GNSS/RTK-Rover', 'Tachymeter', 'Nivelliergerät', 'Drohne', 'Laser-Entfernungsmesser'],
  outdoor: ['GNSS-Empfänger', 'Laser-Entfernungsmesser', 'Drohne'],
};
export const ROLE_PRESETS = ['Leckorter', 'Messtechniker', 'Monteur', 'Bauleiter', 'Vermesser', 'Helfer'];

const read = () => { try { return { staff: [], devices: [], ...JSON.parse(localStorage.getItem(KEY) || '{}') }; } catch { return { staff: [], devices: [] }; } };
export const getCrew = read;
export function saveCrew(fn) {
  const v = { ...read(), ...fn(read()) };
  localStorage.setItem(KEY, JSON.stringify(v));
  window.dispatchEvent(new Event('giss-crew'));
}
export const addStaff = (p) => saveCrew((c) => ({ staff: [...c.staff, { id: uid('st'), ...p }] }));
export const addDevice = (p) => saveCrew((c) => ({ devices: [...c.devices, { id: uid('dv'), ...p }] }));
export const delCrew = (kind, id) => saveCrew((c) => ({ [kind]: c[kind].filter((x) => x.id !== id) }));

export function useCrew() {
  const [v, setV] = useState(read);
  useEffect(() => { const on = () => setV(read()); window.addEventListener('giss-crew', on); return () => window.removeEventListener('giss-crew', on); }, []);
  return v;
}

// order times: depart -> arrive -> start (pauses) -> end -> back
export const STEPS = ['depart', 'arrive', 'start', 'end', 'back'];
export function jobTimes(job = {}) {
  const e = job.ev || {}, min = (a, b) => (a && b && b > a ? (b - a) / 60000 : 0);
  const pause = (job.pauses || []).reduce((s, [a, b]) => s + min(a, b || (e.end || Date.now())), 0);
  return { travel: min(e.depart, e.arrive) + min(e.end, e.back), pause, work: Math.max(0, min(e.start, e.end || (e.start ? Date.now() : 0)) - pause) };
}
export const hm = (m) => `${Math.floor(m / 60)}:${String(Math.round(m % 60)).padStart(2, '0')} h`;
// calibration valid 12 months; 'due' = expired, 'soon' = expires within 30 days
export const calNext = (d) => (d.calib ? new Date(new Date(d.calib).getTime() + 365 * 864e5) : null);
export const calDue = (d) => !!d.calib && Date.now() > calNext(d).getTime();
export const calSoon = (d) => !!d.calib && !calDue(d) && calNext(d).getTime() - Date.now() < 30 * 864e5;
// staff cost = (work + travel hours) x hourly rate of each selected person
export function staffCost(job = {}, crew = read()) {
  const tm = jobTimes(job), h = (tm.work + tm.travel) / 60;
  const rated = crew.staff.filter((x) => (job.staff || []).includes(x.id) && Number(x.rate) > 0);
  return rated.length ? rated.reduce((s, x) => s + h * Number(x.rate), 0) : null;
}
